import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from "./contexts/NotificationContext";
import NotificationsPage from "./pages/NotificationsPage";
import Layout from './components/Layout/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import RegisterVerifyOtp from './pages/RegisterVerifyOtp';
import ProfessionalDetails from './pages/ProfessionalDetails';
import SelectSubscription from './pages/SelectSubscription';
import Patients from './pages/Patients';
import PatientProfile from './pages/PatientProfile';
import AddClinicalNote from './pages/AddClinicalNote';
import Reports from './pages/Reports';
import ReportView from './pages/ReportView';
import ReportEdit from './pages/ReportEdit';
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useEffect } from "react";
import SuperAdminDashboard from "./components/Dashboard/SuperAdminDashboard";
import Dashboard from "./pages/Dashboard";
import Profile from "./pages/Profile";
import Doctors from "./pages/Doctor";
import DoctorForm from "./pages/DoctorForm";
import PublicUsers from "./pages/PublicUser";
import Users from "./pages/Users";
import UserForm from "./pages/UserForm";
import SubscriptionListingPage from "./pages/SubscriptionListingPage";
import SubscriptionDashboard from "./pages/SubscriptionDashboard";
import PatientForm from "./pages/PatientForm";
import ForgotPassword from "./pages/ForgotPassword";
// import VerifyOtp from "./pages/VerifyOtp";
import SetPassword from "./pages/SetPassword";
import ResetPassword from "./pages/ResetPassword";
import SuperAdminGuard from "./components/guards/SuperAdminGuard";
import AuditActivityPage from "./pages/AuditActivityPage";
import RoleProtectedRoute from "./components/guards/RoleProtectedRoute";
import { ROLES } from "./constants/roles";
import LegalDocumentsPage from "./pages/LegalDocumentsPage";
import { Link } from "react-router-dom";



// Normalizes any route to kebab-case, but DO NOT normalize /login to avoid refresh
const RouteNormalizer: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const pathname = location.pathname;

    // Prevent refresh during login, register and password reset
    if (
      pathname === "/login" ||
      pathname === "/admin/login" ||
      pathname === "/register" ||
      pathname === "/register/verify-otp" ||
      pathname === "/register/professional-details" ||
      pathname === "/register/select-plan" ||
      pathname === "/set-password"
    ) return;

    const normalizedPath = pathname
      .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
      .replace(/[_\s]+/g, "-")
      .replace(/-+/g, "-")
      .toLowerCase();

    if (normalizedPath !== pathname) {
      navigate(normalizedPath, { replace: true });
    }
  }, [location.pathname, navigate]);

  return null;
};

// Protected Route - requires authentication and validation
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If user is not validated (false or undefined), redirect to set-password page
  if (user.isValidated !== true) {
    return <Navigate to="/set-password" replace />;
  }

  return <>{children}</>;
};

// Password Reset Route - requires authentication but allows unvalidated users
const PasswordResetRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If user is already validated, redirect to dashboard
  if (user.isValidated === true) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();
  const location = useLocation(); // SAFE: Hook at top-level always

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // If logged in AND validated AND user is on /login, /admin/login or /register → Redirect to dashboard
  // Don't redirect if user is not validated (they need to set password first)
  if (user && user.isValidated === true && (location.pathname === "/login" || location.pathname === "/admin/login" || location.pathname === "/register")) {
    return <Navigate to="/dashboard" replace />;
  }
  

  return <>{children}</>;
};

const LegalRoute: React.FC = () => {
  const { user } = useAuth();
  if (user) {
    if (user.role === ROLES.SUPERADMIN) {
      return <Navigate to="/dashboard" replace />;
    }
    return (
      <ProtectedRoute>
        <Layout>
          <LegalDocumentsPage />
        </Layout>
      </ProtectedRoute>
    );
  }
  return (
    <div className="min-h-screen bg-[#F8FAFA] p-4 sm:p-6 md:p-8">
      <div className="max-w-5xl mx-auto mb-6 flex items-center justify-between">
        <img
          src="/logo/DiagnomIQ.png"
          alt="DiagnomIQ"
          className="h-9 w-auto object-contain"
        />
        <Link
          to="/login"
          className="text-sm font-medium text-primary-600 hover:text-primary-700"
        >
          &larr; Back to Login
        </Link>
      </div>
      <div className="max-w-5xl mx-auto">
        <LegalDocumentsPage />
      </div>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <NotificationProvider>
      <Router>
        <RouteNormalizer />
        <ToastContainer
          position="top-right"
          autoClose={3000}
          theme="light"
          closeButton
          className="diag-container"
        />

        <Routes>
          {/* Public Login */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />

          {/* Admin Login */}
          <Route
            path="/admin/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />

          {/* Public Register */}
          <Route
            path="/register"
            element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            }
          />

          {/* Public Register - Verify Email OTP */}
          <Route
            path="/register/verify-otp"
            element={
              <PublicRoute>
                <RegisterVerifyOtp />
              </PublicRoute>
            }
          />

          {/* Doctor Register - Professional Details (after email verification) */}
          <Route
            path="/register/professional-details"
            element={
              <PublicRoute>
                <ProfessionalDetails />
              </PublicRoute>
            }
          />

          {/* Public Register - Select Subscription Plan */}
          <Route
            path="/register/select-plan"
            element={
              <PublicRoute>
                <SelectSubscription />
              </PublicRoute>
            }
          />

          {/* Set Password - Requires auth but allows unvalidated users */}
          <Route
            path="/set-password"
            element={
              <PasswordResetRoute>
                <SetPassword />
              </PasswordResetRoute>
            }
          />

          {/* Super Admin Dashboard */}
          <Route
            path="/set-passwordin-dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <SuperAdminDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Consultant Dashboard */}
          {/* <Route
            path="/consultant-dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <ConsultantDashboard />
                </Layout>
              </ProtectedRoute>
            }
          /> */}

          {/* Junior Doctor Dashboard */}
          {/* <Route
            path="/junior-doctor-dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <JuniorDoctorDashboard />
                </Layout>
              </ProtectedRoute>
            }
          /> */}

          {/* Default Dashboard */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Layout>  
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Profile */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Layout>
                  <Profile />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Legal Documents */}
          <Route path="/legal" element={<LegalRoute />} />

          <Route path="/forgot-password" element={<ForgotPassword />} />
          {/* <Route path="/verify-otp" element={<VerifyOtp />} /> */}
          <Route path="/reset-password" element={<ResetPassword />} />


          {/* Doctors - Super Admin Only */}
          <Route
            path="/doctors"
            element={
              <ProtectedRoute>
                <SuperAdminGuard>
                  <Layout>
                    <Doctors />
                  </Layout>
                </SuperAdminGuard>
              </ProtectedRoute>
            }
          />

          {/* Public Users - Super Admin Only */}
          <Route
            path="/public-users"
            element={
              <ProtectedRoute>
                <SuperAdminGuard>
                  <Layout>
                    <PublicUsers />
                  </Layout>
                </SuperAdminGuard>
              </ProtectedRoute>
            }
          />

          {/* Users - Super Admin Only */}
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <SuperAdminGuard>
                  <Layout>
                    <Users />
                  </Layout>
                </SuperAdminGuard>
              </ProtectedRoute>
            }
          />

          {/* Subscriptions Dashboard - Super Admin Only */}
          <Route
            path="/subscriptions/dashboard"
            element={
              <ProtectedRoute>
                <SuperAdminGuard>
                  <Layout>
                    <SubscriptionDashboard />
                  </Layout>
                </SuperAdminGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscriptions"
            element={
              <ProtectedRoute>
                <SuperAdminGuard>
                  <Layout>
                    <SubscriptionDashboard />
                  </Layout>
                </SuperAdminGuard>
              </ProtectedRoute>
            }
          />

          {/* Subscription Listing - Super Admin Only */}
          <Route
            path="/subscriptions/listing"
            element={
              <ProtectedRoute>
                <SuperAdminGuard>
                  <Layout>
                    <SubscriptionListingPage />
                  </Layout>
                </SuperAdminGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscription-listing"
            element={<Navigate to="/subscriptions/listing" replace />}
          />
          <Route
            path="/admin/subscriptions"
            element={<Navigate to="/subscriptions/dashboard" replace />}
          />
          <Route
            path="/subscriptions/plans"
            element={<Navigate to="/subscriptions/dashboard" replace />}
          />

          {/* Add Doctor - Super Admin Only */}
          <Route
            path="/doctors/new"
            element={
              <ProtectedRoute>
                <SuperAdminGuard>
                  <Layout>
                    <DoctorForm />
                  </Layout>
                </SuperAdminGuard>
              </ProtectedRoute>
            }
          />

          {/* Edit Doctor - Super Admin Only */}
          <Route
            path="/doctors/:id/edit"
            element={
              <ProtectedRoute>
                <SuperAdminGuard>
                  <Layout>
                    <DoctorForm />
                  </Layout>
                </SuperAdminGuard>
              </ProtectedRoute>
            }
          />

          {/* Add User - Super Admin Only */}
          <Route
            path="/users/new"
            element={
              <ProtectedRoute>
                <SuperAdminGuard>
                  <Layout>
                    <UserForm />
                  </Layout>
                </SuperAdminGuard>
              </ProtectedRoute>
            }
          />

          {/* Edit User - Super Admin Only */}
          <Route
            path="/users/:id/edit"
            element={
              <ProtectedRoute>
                <SuperAdminGuard>
                  <Layout>
                    <UserForm />
                  </Layout>
                </SuperAdminGuard>
              </ProtectedRoute>
            }
          />

          {/* Other existing protected pages */}
          <Route
            path="/patients"
            element={
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={[ROLES.DOCTOR]}>
                  <Layout>
                    <Patients />
                  </Layout>
                </RoleProtectedRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/patients/new"
            element={
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={[ROLES.DOCTOR]}>
                  <Layout>
                    <PatientForm />
                  </Layout>
                </RoleProtectedRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/patients/create"
            element={
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={[ROLES.DOCTOR]}>
                  <Layout>
                    <PatientForm />
                  </Layout>
                </RoleProtectedRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/patients/:id/edit"
            element={
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={[ROLES.DOCTOR]}>
                  <Layout>
                    <PatientForm />
                  </Layout>
                </RoleProtectedRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/patients/:id"
            element={
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={[ROLES.DOCTOR]}>
                  <Layout>
                    <PatientProfile />
                  </Layout>
                </RoleProtectedRoute>
              </ProtectedRoute>
            }
          />
<Route
  path="/patients/:id/notes/new"
  element={
    <ProtectedRoute>
      <RoleProtectedRoute allowedRoles={[ROLES.DOCTOR]}>
        <Layout>
          <AddClinicalNote />
        </Layout>
      </RoleProtectedRoute>
    </ProtectedRoute>
  }
/>


          {/* <Route path="/patients" element={<ProtectedRoute><Layout><RoleProtectedRoute allowedRoles={[ROLES.DOCTOR]}><Patients /> </RoleProtectedRoute></Layout></ProtectedRoute>} /> */}
          {/* <Route path="/patients/:id" element={<ProtectedRoute><Layout><PatientProfile /></Layout></ProtectedRoute>} /> */}
          {/* <Route path="/patients/:id/notes/new" element={<ProtectedRoute><Layout><AddClinicalNote /></Layout></ProtectedRoute>} /> */}

          <Route path="/notifications" element={<ProtectedRoute><Layout><NotificationsPage /></Layout></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute><Layout><Reports /></Layout></ProtectedRoute>} />
          <Route path="/reports/:id" element={<ProtectedRoute><Layout><ReportView /></Layout></ProtectedRoute>} />
          <Route path="/reports/:id/edit" element={<ProtectedRoute><Layout><ReportEdit /></Layout></ProtectedRoute>} />
          <Route path="/doctors" element={<SuperAdminGuard><ProtectedRoute><Layout><Doctors /></Layout></ProtectedRoute></SuperAdminGuard>} />
          {/* <Route path="/activity" element={<SuperAdminGuard><ProtectedRoute><Layout><AuditActivityPage /></Layout></ProtectedRoute></SuperAdminGuard>} /> */}
          {/* <Route path="/imaging" element={<ProtectedRoute><Layout><Imaging /></Layout></ProtectedRoute>} /> */}
          <Route path="/activity" element={<ProtectedRoute><Layout><RoleProtectedRoute allowedRoles={[ROLES.SUPERADMIN, ROLES.DOCTOR]}><AuditActivityPage /></RoleProtectedRoute></Layout></ProtectedRoute>} />
          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
          </NotificationProvider>
    </AuthProvider>
  );
};

export default App;